/* ============================================================
   MYNA Cart Drawer v4 — SPEED + ACCURACY
   v4 changes:
   - Instant drawer open (renders cached cart, syncs in background)
   - Debounced quantity +/- with request sequencing — rapid clicks
     send ONE request with the final qty; stale responses discarded
   - Surgical DOM updates on qty change (no full re-render → no lost
     clicks, no animation replay)
   - Event delegation bound once (no rebinding per render)
   - Item fade-in animation only on drawer open, not on every update
   Carried over from v3: Shopify Cart API + optimistic updates,
   site-wide wiring, Dawn notification suppression, Magic Checkout.
   ============================================================ */
(function () {
  'use strict';

  var FREE_SHIP = 250000;
  var state = { cart: {}, open: false, animate: false };
  var upsells = window.mcUpsells || [];

  var scrim    = document.getElementById('mc-scrim');
  var drawer   = document.getElementById('mc-drawer');
  var bodyEl   = document.getElementById('mc-body');
  var billEl   = document.getElementById('mc-bill-el');
  var footEl   = document.getElementById('mc-foot-wrap');
  var cntEl    = document.getElementById('mc-count');
  var ckaEl    = document.getElementById('mc-ckamt');
  var closeBtn = document.getElementById('mc-close');
  var ckoBtn   = document.getElementById('mc-checkout-btn');
  if (!drawer) return;

  /* ---- Utils ---- */
  function fmt(p) { return '₹' + (p / 100).toLocaleString('en-IN'); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];
    });
  }
  function imgUrl(src, size) {
    if (!src) return '';
    return src.replace(/(\.[a-zA-Z]{3,4})(\?.*)?$/, '_' + size + '$1$2');
  }

  /* ---- Cart API ---- */
  /* Keep a ref to the ORIGINAL fetch BEFORE we patch it below */
  var _rawFetch = window.fetch.bind(window);

  function fetchCart() {
    return _rawFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function apiChange(key, qty) {
    return _rawFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); });
  }
  function apiAdd(vid, qty) {
    return _rawFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: vid, quantity: qty || 1 })
    }).then(function (r) { return r.json(); });
  }

  /* ---- Optimistic state update ---- */
  function applyOptimistic(key, qty) {
    var items = (state.cart.items || []).map(function (i) {
      return i.key === key ? Object.assign({}, i, { quantity: qty }) : i;
    }).filter(function (i) { return i.quantity > 0; });
    state.cart = Object.assign({}, state.cart, {
      items: items,
      item_count:           items.reduce(function (s, i) { return s + i.quantity; }, 0),
      total_price:          items.reduce(function (s, i) { return s + i.final_price * i.quantity; }, 0),
      original_total_price: items.reduce(function (s, i) { return s + i.original_price * i.quantity; }, 0)
    });
  }

  function setCount(n) {
    if (cntEl) cntEl.textContent = n;
    document.querySelectorAll('[data-mc-count],[data-cart-count],#alk-cart-count,.cart-count,.header__cart-count').forEach(function (el) {
      el.textContent = n;
      if (n > 0) el.removeAttribute('style');
      else el.style.display = 'none';
    });
  }

  /* ================================================================
     QUANTITY CHANGE PIPELINE — debounce + sequencing (v4)
     Rapid +/- clicks update the UI instantly (optimistic) but only ONE
     network request is sent per item, carrying the FINAL quantity.
     A global sequence token discards out-of-order responses so an old
     reply can never overwrite newer state (the v3 accuracy bug).
     ================================================================ */
  var qtyTimers = {};
  var sendSeq = 0;

  function pendingCount() {
    var n = 0, k;
    for (k in qtyTimers) if (qtyTimers[k]) n++;
    return n;
  }

  function sendChange(key, qty) {
    qtyTimers[key] = null;
    var t = ++sendSeq;
    apiChange(key, qty).then(function (c) {
      /* Only the latest in-flight response may touch state, and only
         when no further changes are queued */
      if (t === sendSeq && !pendingCount()) { state.cart = c; render(); }
    }).catch(function () {
      if (t === sendSeq && !pendingCount()) {
        fetchCart().then(function (c) { state.cart = c; render(); });
      }
    });
  }

  function queueChange(key, qty, immediate) {
    if (qtyTimers[key]) clearTimeout(qtyTimers[key]);
    if (immediate) { qtyTimers[key] = null; sendChange(key, qty); return; }
    qtyTimers[key] = setTimeout(function () { sendChange(key, qty); }, 260);
  }

  /* Surgical DOM update — touch only the changed row, keep the rest */
  function surgicalQty(key) {
    if (!bodyEl) return;
    var item = (state.cart.items || []).filter(function (i) { return i.key === key; })[0];
    var row = bodyEl.querySelector('.mc-item[data-key="' + key + '"]');
    if (!row) { render(); return; }
    if (!item) {
      row.classList.add('mc-removing');
      setTimeout(function () {
        if (row.parentNode) row.parentNode.removeChild(row);
        if (!(state.cart.items || []).length) render();
      }, 240);
      return;
    }
    var num = row.querySelector('.mc-qty-num');
    if (num) num.textContent = item.quantity;
    var qm = row.querySelector('.mc-qm');
    var qp = row.querySelector('.mc-qp');
    if (qm) {
      qm.setAttribute('data-qty', item.quantity - 1);
      if (item.quantity <= 1) qm.setAttribute('disabled', '');
      else qm.removeAttribute('disabled');
    }
    if (qp) qp.setAttribute('data-qty', item.quantity + 1);
  }

  function changeQty(key, qty, immediate) {
    applyOptimistic(key, qty);
    surgicalQty(key);
    renderBill();
    queueChange(key, qty, immediate);
  }

  /* ---- Open / Close ---- */
  function openCart() {
    if (state.open) return;
    state.open = true;
    state.animate = true;
    /* v4: open INSTANTLY from cached state, sync in background */
    drawer.classList.add('on'); scrim.classList.add('on');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
    render();
    fetchCart().then(function (c) {
      if (pendingCount()) return; /* don't stomp queued local changes */
      state.cart = c; render();
    });
  }
  function closeCart() {
    if (!state.open) return;
    state.open = false;
    drawer.classList.remove('on'); scrim.classList.remove('on');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
  }

  /* ---- Render ---- */
  function render() {
    var c = state.cart;
    var items = c.items || [];
    var total = c.total_price || 0;
    var anim = state.animate;
    state.animate = false;

    setCount(c.item_count || 0);
    if (ckaEl) ckaEl.textContent = fmt(total);

    if (!items.length) {
      if (footEl) footEl.style.display = 'none';
      if (billEl) billEl.innerHTML = '';
      if (bodyEl) bodyEl.innerHTML =
        '<div class="mc-empty">' +
        '<p class="mc-empty-msg">Your bag<br>is empty.</p>' +
        '<a href="/collections/all" class="mc-empty-btn">Explore the Collection</a>' +
        '</div>';
      return;
    }
    if (footEl) footEl.style.display = '';

    /* Items */
    var h = '<div class="mc-items-lbl">In Your Bag</div>';
    items.forEach(function (item, i) {
      var pct = item.original_price > item.final_price
        ? Math.round((item.original_price - item.final_price) / item.original_price * 100) : 0;
      var thumb = imgUrl(item.image, '160x180');
      var animStyle = anim ? ' style="animation:mcFadeUp .4s cubic-bezier(.33,1,.68,1) ' + (i * 0.07) + 's both"' : '';
      h += '<div class="mc-item" data-key="' + esc(item.key) + '"' + animStyle + '>';
      h += '<div class="mc-thumb"><img src="' + esc(thumb) + '" alt="' + esc(item.product_title) + '" loading="lazy"></div>';
      h += '<div class="mc-ibody"><div class="mc-itop"><div>';
      if (item.product_type) h += '<div class="mc-itag">' + esc(item.product_type) + '</div>';
      h += '<div class="mc-iname">' + esc(item.product_title) + '</div>';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        h += '<div class="mc-ivariant">' + esc(item.variant_title) + '</div>';
      }
      h += '</div>';
      h += '<button type="button" class="mc-irem" data-key="' + esc(item.key) + '" aria-label="Remove">';
      h += '<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>';
      h += '<div class="mc-ibot"><div>';
      h += '<span class="mc-iprice">' + fmt(item.final_price) + '</span>';
      if (item.original_price > item.final_price) {
        h += '<span class="mc-iprice-orig">' + fmt(item.original_price) + '</span>';
        h += '<br><span class="mc-idisc">' + pct + '% Off</span>';
      }
      h += '</div><div class="mc-qty">';
      h += '<button type="button" class="mc-qm" data-key="' + esc(item.key) + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease"' + (item.quantity <= 1 ? ' disabled' : '') + '>';
      h += '<svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"><path d="M5 12h14"/></svg></button>';
      h += '<span class="mc-qty-num">' + item.quantity + '</span>';
      h += '<button type="button" class="mc-qp" data-key="' + esc(item.key) + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase">';
      h += '<svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg></button>';
      h += '</div></div></div></div>';
    });

    /* Upsells */
    if (upsells && upsells.length) {
      var inCart = items.map(function (i) { return i.variant_id; });
      var avail  = upsells.filter(function (u) { return u && inCart.indexOf(Number(u.id)) === -1; });
      if (avail.length) {
        h += '<div class="mc-ctl"><div class="mc-ctl-lbl">Complete the Look</div><div class="mc-ctl-track">';
        avail.forEach(function (u) {
          h += '<div class="mc-ucard">';
          h += '<div class="mc-ucard-img"><img src="' + esc(u.img || '') + '" alt="' + esc(u.name) + '" loading="lazy"></div>';
          h += '<div class="mc-ucard-name">' + esc(u.name) + '</div>';
          h += '<div class="mc-ucard-bot"><span class="mc-ucard-price">' + fmt(Number(u.price)) + '</span>';
          h += '<button type="button" class="mc-ucard-add" data-vid="' + esc(u.id) + '" aria-label="Add to bag">';
          h += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg></button></div></div>';
        });
        h += '</div></div>';
      }
    }
    if (bodyEl) bodyEl.innerHTML = h;

    /* Bill */
    renderBill();
  }

  function renderBill() {
    if (!billEl) return;
    var c = state.cart;
    var items = c.items || [];
    var total = c.total_price || 0;
    var orig  = c.original_total_price || total;
    var disc  = orig - total;
    var itemCount = items.reduce(function (s, i) { return s + i.quantity; }, 0);
    var shipping  = total >= FREE_SHIP ? 0 : 9900;
    if (ckaEl) ckaEl.textContent = fmt(total);
    setCount(c.item_count || 0);
    var b = '';
    b += '<div class="mc-bill-hd">Price Details <span class="mc-bill-hd-n">· ' + itemCount + ' item' + (itemCount === 1 ? '' : 's') + '</span></div>';
    b += '<div class="mc-bill-row"><span class="mc-bl">MRP</span><span class="mc-bv">' + fmt(orig) + '</span></div>';
    if (disc > 0) b += '<div class="mc-bill-row mc-disc"><span class="mc-bl">Product Discount</span><span class="mc-bv">− ' + fmt(disc) + '</span></div>';
    if (total >= FREE_SHIP) {
      b += '<div class="mc-bill-row"><span class="mc-bl">Delivery Charges</span><span class="mc-bv mc-free-lbl">FREE ✓</span></div>';
    } else {
      var rem = FREE_SHIP - total; var pct2 = Math.min(100, Math.round(total / FREE_SHIP * 100));
      b += '<div class="mc-bill-row"><span class="mc-bl">Delivery Charges</span><span class="mc-bv">' + fmt(9900) + '</span></div>';
      b += '<div class="mc-ship-bar"><div class="mc-ship-fill" style="width:' + pct2 + '%"></div></div>';
      b += '<p class="mc-ship-note">Add <strong>' + fmt(rem) + '</strong> more for FREE delivery</p>';
    }
    b += '<div class="mc-bill-hr"></div>';
    b += '<div class="mc-bill-total"><span class="mc-bt mc-bl">Total Amount</span><span class="mc-bv">' + fmt(total + shipping) + '</span></div>';
    if (disc > 0) b += '<div class="mc-bill-save">🎉 You\'re saving <strong>' + fmt(disc) + '</strong> on this order!</div>';
    billEl.innerHTML = b;
  }

  /* ---- Event delegation — bound ONCE, survives every re-render ---- */
  if (bodyEl) bodyEl.addEventListener('click', function (e) {
    var t;
    if ((t = e.target.closest('.mc-irem'))) {
      var row = bodyEl.querySelector('.mc-item[data-key="' + t.getAttribute('data-key') + '"]');
      if (row) row.classList.add('mc-removing');
      changeQty(t.getAttribute('data-key'), 0, true);
      return;
    }
    if ((t = e.target.closest('.mc-qm'))) {
      var q = parseInt(t.getAttribute('data-qty'), 10);
      if (q >= 0) changeQty(t.getAttribute('data-key'), q);
      return;
    }
    if ((t = e.target.closest('.mc-qp'))) {
      changeQty(t.getAttribute('data-key'), parseInt(t.getAttribute('data-qty'), 10));
      return;
    }
    if ((t = e.target.closest('.mc-ucard-add'))) {
      if (t.classList.contains('added')) return;
      t.classList.add('added');
      t.innerHTML = '<svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round"><path d="M5 13l4 4L19 7"/></svg>';
      apiAdd(parseInt(t.getAttribute('data-vid'), 10), 1)
        .then(fetchCart)
        .then(function (c) { if (!pendingCount()) { state.cart = c; render(); } });
      return;
    }
  });

  /* ================================================================
     MAGIC CHECKOUT — Razorpay one-click payment
     Flow: click → load Razorpay SDK → create order (backend optional)
           → open Razorpay popup → on success → complete order + show confirmation
     ================================================================ */

  function loadRzpScript(cb) {
    if (window.Razorpay) { cb(true); return; }
    var s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = function () { cb(true); };
    s.onerror = function () { cb(false); };
    document.head.appendChild(s);
  }

  function setBtnLoading(on) {
    if (!ckoBtn) return;
    var lbl = ckoBtn.querySelector('.mc-mbtn-l');
    ckoBtn.disabled = on;
    if (on) {
      ckoBtn.classList.add('mc-loading');
      if (lbl) lbl.textContent = 'Opening Checkout…';
    } else {
      ckoBtn.classList.remove('mc-loading');
      if (lbl) lbl.textContent = 'Proceed to Checkout';
    }
  }

  function showCheckoutSuccess(orderName) {
    if (bodyEl) bodyEl.innerHTML =
      '<div class="mc-success">' +
      '<div class="mc-success-icon">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>' +
      '</div>' +
      '<div class="mc-success-title">Order Placed!</div>' +
      '<div class="mc-success-sub">' + (orderName ? 'Order ' + esc(orderName) + ' confirmed' : 'Your order is confirmed.') + '</div>' +
      '<a href="/collections/all" class="mc-empty-btn" style="margin-top:28px">Continue Shopping</a>' +
      '</div>';
    if (footEl) footEl.style.display = 'none';
    /* Clear Shopify cart */
    _rawFetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
    state.cart = { items: [], item_count: 0, total_price: 0, original_total_price: 0 };
    setCount(0);
  }

  function openRazorpayCheckout(orderId) {
    var cfg = window.MYNA_CONFIG || {};
    var total = state.cart.total_price || 0;
    var cartSnapshot = JSON.parse(JSON.stringify(state.cart)); /* snapshot before popup */
    var options = {
      key: cfg.rzpKey || '',
      amount: total,
      currency: cfg.currency || 'INR',
      name: cfg.shopName || 'MYNA',
      description: 'Jewellery Order',
      handler: function (response) {
        setBtnLoading(true);
        var backendUrl = cfg.backendUrl || '';
        if (backendUrl) {
          _rawFetch(backendUrl + '/api/complete-order', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id:   response.razorpay_order_id || orderId || '',
              razorpay_signature:  response.razorpay_signature,
              cart: cartSnapshot
            })
          })
            .then(function (r) { return r.json(); })
            .then(function (d) { showCheckoutSuccess(d.order_name || d.name || ''); })
            .catch(function ()  { showCheckoutSuccess(''); });
        } else {
          showCheckoutSuccess('');
        }
      },
      prefill: {},
      theme: { color: '#f5f1ea', backdrop_color: 'rgba(5,5,5,0.85)' },
      modal: {
        ondismiss: function () { setBtnLoading(false); }
      }
    };
    if (orderId) options.order_id = orderId;
    var rzp = new window.Razorpay(options);
    rzp.open();
    setBtnLoading(false);
  }

  function initMagicCheckout() {
    var items = state.cart.items || [];
    if (!items.length) { openCart(); return; }
    var cfg = window.MYNA_CONFIG || {};
    var rzpKey = cfg.rzpKey;
    if (!rzpKey) {
      /* No Razorpay key configured — fall back to Shopify native checkout */
      window.location.href = '/checkout';
      return;
    }
    setBtnLoading(true);
    loadRzpScript(function (ok) {
      if (!ok) { setBtnLoading(false); window.location.href = '/checkout'; return; }
      var backendUrl = cfg.backendUrl || '';
      if (backendUrl) {
        /* Create order on backend for signature verification */
        _rawFetch(backendUrl + '/api/create-razorpay-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: state.cart.total_price, currency: 'INR', cart: state.cart })
        })
          .then(function (r) { return r.json(); })
          .then(function (d) { openRazorpayCheckout(d.id || d.order_id || null); })
          .catch(function ()  { openRazorpayCheckout(null); }); /* fallback: open without order_id */
      } else {
        openRazorpayCheckout(null);
      }
    });
  }

  if (ckoBtn) {
    ckoBtn.addEventListener('click', initMagicCheckout);
  }

  /* ---- Standard event bus ---- */
  if (closeBtn) closeBtn.addEventListener('click', closeCart);
  if (scrim)    scrim.addEventListener('click', closeCart);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && state.open) closeCart(); });
  document.addEventListener('cart:open',       openCart);
  document.addEventListener('cart:updated',    function () { fetchCart().then(function (c) { if (!pendingCount()) { state.cart = c; render(); } }); });
  document.addEventListener('cart:added',      function () { fetchCart().then(function (c) { state.cart = c; render(); openCart(); }); });
  document.addEventListener('theme:cart:open', openCart);

  /* ---- Initial count ---- */
  fetchCart().then(function (c) { state.cart = c; setCount(c.item_count || 0); });

  /* ---- Global API ---- */
  function cartAdd(vid, qty) {
    /* v4: open drawer INSTANTLY, add + sync in background */
    openCart();
    apiAdd(vid, qty || 1)
      .then(fetchCart)
      .then(function (c) { if (!pendingCount()) { state.cart = c; render(); } });
  }
  window.mynaCart = { open: openCart, close: closeCart, add: cartAdd,
    refresh: function () { fetchCart().then(function (c) { state.cart = c; render(); }); } };
  window.mynaOpenCart  = openCart;
  window.mynaCloseCart = closeCart;
  window.mynaAddToCart = cartAdd;
  window.alkAddToCart  = cartAdd;
  window.MYNA = window.MYNA || {};

  /* ================================================================
     SITE-WIDE CART WIRING
     1. Intercept any /cart link click  → open drawer
     2. Intercept native form submits to /cart/add  → AJAX + open
     3. Patch fetch to detect /cart/add  → open drawer after success
     4. Patch XHR   to detect /cart/add  → open drawer after success
     5. Suppress Dawn CartNotification popup
     ================================================================ */

  document.addEventListener('click', function (e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    try {
      var u = new URL(a.href, location.href);
      if (u.origin === location.origin && u.pathname === '/cart') {
        e.preventDefault();
        e.stopImmediatePropagation();
        openCart();
      }
    } catch (_) {}
  }, true);

  document.addEventListener('submit', function (e) {
    if (e.defaultPrevented) return;
    var form = e.target;
    if (!form || !form.action) return;
    try {
      var p = new URL(form.action, location.href).pathname;
      if (p !== '/cart/add') return;
    } catch (_) { return; }
    e.preventDefault();
    var fd = new FormData(form);
    _rawFetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: fd
    }).then(function (r) {
      if (r.ok) {
        openCart();
        fetchCart().then(function (c) { if (!pendingCount()) { state.cart = c; render(); } });
      } else {
        form.submit();
      }
    }).catch(function () { form.submit(); });
  }, false);

  window.fetch = function (resource, init) {
    var url = typeof resource === 'string' ? resource
            : (resource instanceof Request ? resource.url : '');
    var promise = _rawFetch.apply(window, arguments);
    if (url && /\/cart\/add/.test(url)) {
      promise.then(function (r) {
        if (r && r.ok) {
          openCart();
          fetchCart().then(function (c) { if (!pendingCount()) { state.cart = c; render(); } });
        }
      }).catch(function () {});
    }
    return promise;
  };

  var _mcOrigXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && /\/cart\/add/.test(url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) {
          openCart();
          fetchCart().then(function (c) { if (!pendingCount()) { state.cart = c; render(); } });
        }
      });
    }
    return _mcOrigXHROpen.apply(this, arguments);
  };

  function suppressDawnCartNotification() {
    var cn = customElements && customElements.get && customElements.get('cart-notification');
    if (cn && cn.prototype && cn.prototype.renderContents) {
      cn.prototype.renderContents = function () {};
      return true;
    }
    return false;
  }
  if (!suppressDawnCartNotification()) {
    var _mcRetry = setInterval(function () {
      if (suppressDawnCartNotification()) clearInterval(_mcRetry);
    }, 200);
    setTimeout(function () { clearInterval(_mcRetry); }, 5000);
  }

})();
